import { DashboardSection } from '@/components/Dashboard/enums/shared';
import { FolderScope } from '@/enums/folder-scope.enum';

/** Same key `useDashboardSection` reads. */
export const DASHBOARD_SECTION_STORAGE_KEY = 'dashboardCurrentSelection';

/** Which kind page the file browser is on. Stored beside the section, and ignored while the tabs are off. */
export type DashboardPage = 'workflow' | 'tool';

/** Same key `useDashboardSection` reads for {@link DashboardPage}. */
export const DASHBOARD_PAGE_STORAGE_KEY = 'dashboardCurrentPage';

/** My, Workspace, and Shared with me. Toolkit and Trash are their own pages, not one of these. */
export const isFileScopeSection = (section: DashboardSection): boolean =>
  section === DashboardSection.RecentRecipes ||
  section === DashboardSection.WorkspaceFiles ||
  section === DashboardSection.SharedWithMe;

export const isDashboardPage = (value: string | null): value is DashboardPage =>
  value === 'workflow' || value === 'tool';

type FilesBrowserRecipe = {
  folderId?: string | null;
  scope?: FolderScope | null;
};

type FilesBrowserLocationState = {
  dashboardSection: DashboardSection;
  dashboardPage: DashboardPage;
};

type FilesBrowserNavigate = (path: string, options: { state: FilesBrowserLocationState }) => void;

export const getDashboardSectionFromScope = (scope?: FolderScope | null): DashboardSection => {
  if (scope === FolderScope.WORKSPACE) {
    return DashboardSection.WorkspaceFiles;
  }

  return DashboardSection.RecentRecipes;
};

/**
 * Leave the canvas for the file browser. Carries the scope so a root-level workspace file
 * (no `folderId`) still opens Workspace, not My. `page` is which kind the caller was looking
 * at: the canvas opens Workflows, the tool view opens Tools. A guest leaves with a full page
 * load, so the page is written where the next dashboard will read it; the section is left
 * alone unless the file is a workspace file.
 */
export const navigateToFilesBrowser = (
  navigate: FilesBrowserNavigate,
  recipe: FilesBrowserRecipe,
  isOwner: boolean,
  page: DashboardPage = 'workflow',
): void => {
  // Workspace folders are shared; a personal workflow's folder belongs only to its owner.
  const isWorkspaceWorkflow = recipe.scope === FolderScope.WORKSPACE;
  const path = (isOwner || isWorkspaceWorkflow) && recipe.folderId ? `/folders/${recipe.folderId}` : '/';

  if (!isOwner) {
    // Like goToTool, leave the live canvas with a full page load: a router transition can stall here.
    if (isWorkspaceWorkflow) {
      localStorage.setItem(DASHBOARD_SECTION_STORAGE_KEY, DashboardSection.WorkspaceFiles);
    }
    localStorage.setItem(DASHBOARD_PAGE_STORAGE_KEY, page);
    window.location.assign(path);
    return;
  }

  navigate(path, {
    state: { dashboardSection: getDashboardSectionFromScope(recipe.scope), dashboardPage: page },
  });
};
